import * as React from 'react';

import { cn } from '../../lib/utils';
import { utcTimeToLocalPreview } from '../../utils/dateHelpers';
import { Input } from './input';
import { Label } from './label';

const dayOffsetSuffix = (offset: number): string => {
  if (offset > 0) return ' (next day)';
  if (offset < 0) return ' (previous day)';
  return '';
};

export interface TimeInputProps
  extends Omit<React.ComponentProps<'input'>, 'type' | 'onChange' | 'value'> {
  /** Field label rendered above the input. */
  label: string;
  /** HH:MM (24h) value. UTC. */
  value: string;
  onChange: (value: string) => void;
  /** Optional descriptive text rendered under the field. */
  description?: string;
  /** Optional error message; when set, the input is marked aria-invalid. */
  error?: string | null;
  /** Hide the inline local-time preview hint. Defaults to false. */
  hideLocalPreview?: boolean;
  /** Optional class on the wrapping container. */
  containerClassName?: string;
}

/**
 * UTC time-of-day input with an inline local-time preview.
 *
 * Wraps the shadcn `Input` so styling stays consistent. Always treats `value`
 * as UTC HH:MM and surfaces the user's browser-local equivalent next to the
 * timezone label, e.g. "UTC — your local time is 11:00 SAST".
 *
 * Use anywhere a user enters a recurring time (scheduled jobs, alerts,
 * reports). For one-off datetimes, use a date+time picker instead.
 */
function TimeInput({
  id,
  label,
  value,
  onChange,
  description,
  error,
  hideLocalPreview,
  className,
  containerClassName,
  ...inputProps
}: TimeInputProps) {
  const reactId = React.useId();
  const inputId = id ?? reactId;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;

  const preview = React.useMemo(
    () => (hideLocalPreview ? null : utcTimeToLocalPreview(value)),
    [hideLocalPreview, value],
  );

  return (
    <div className={cn('space-y-2', containerClassName)}>
      <Label htmlFor={inputId}>{label}</Label>
      <Input
        id={inputId}
        type="time"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={
          [error ? errorId : null, preview ? hintId : null]
            .filter(Boolean)
            .join(' ') || undefined
        }
        className={cn('w-[140px]', className)}
        {...inputProps}
      />
      {!hideLocalPreview && (
        <p id={hintId} className="text-xs text-muted-foreground">
          {preview
            ? `UTC — your local time is ${preview.localHHmm}${preview.abbreviation ? ` ${preview.abbreviation}` : ''}${dayOffsetSuffix(preview.dayOffset)}`
            : 'UTC'}
        </p>
      )}
      {description && !error && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      {error && (
        <p id={errorId} className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

export { TimeInput };
